import { useContext, useEffect, useState } from "react";
import { ISessionStateContext, SessionStateContext } from "../../../Core/State/SessionStateContext";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { Loader } from "../Loader/Loader";
import { Icon, Image, ImageFit, ITextFieldProps, Link, PrimaryButton, Stack, Text, TextField } from "@fluentui/react";
import { SystemDataLoadingStatus, SystemUserType } from "../../../Model/SystemModels";
import { SystemCore } from "../../../Core/System/SystemCore";
import LogoSoSmart from '../../../Assets/Images/logoSoSmart.png';
import { CommonFunctions } from "../../../ApplicationCode/Common/CommonFunctions";
import { MessageType, ToastService } from "../../../Core/Toast/ToastService";
import { LoginActionType, UserCredential } from "../../../Model/SecurityModel";
import { HttpHelper } from "../../../Core/Http/HttpHelper";
import { lgLoginBoxContainer, lgLoginBoxTextFieldStyles, lgLoginButtonStyles, lgSubTitleStyles, lgTitleStyles, loginBoxContainer, loginBoxPageContainer, loginBoxTextFieldStyles, loginButtonStyles, loginLogoContainer, mdLoginBoxContainer, mdLoginBoxTextFieldStyles, mdLoginButtonStyles, mdSubTitleStyles, mdTitleStyles, privateFormContainer, registerLinkStyles, smLoginBoxContainer, smLoginBoxTextFieldStyles, smLoginButtonStyles, smSubTitleStyles, smTitleStyles, subTitleStyles, titleStyles, smallWelcomeContainerStyles, xlLoginBoxContainer, xlLoginBoxTextFieldStyles, xlLoginButtonStyles, xlSubTitleStyles, xlTitleStyles, xxlLoginBoxContainer, xxlLoginBoxTextFieldStyles, xxlLoginButtonStyles, xxlSubTitleStyles, xxlTitleStyles, welcomeContainerStyles, wrongPasswordContainerStyles } from "./Styles/LoginBoxStyles";
import { useBreakpoint } from "../Container/ContainerStyles";
import { BreakpointType } from "../Container/ContainerFluid";

export interface ILoginForm {
    username: string;
    password: string;
    userNameErrorMessage?: string;
    passwordErrorMessage?: string;
    saveInProgress: boolean;
    showPrivateLoginBox: boolean;
    showPasswordDismiss: boolean;
    confirmButtonDisabled: boolean;
    handleUsernameChange: (newValue?: string | undefined) => void;
    handlePasswordChange: (newValue?: string | undefined) => void;
    handleUserResetPassword: () => void;
    handleSubmit: (formActionType: LoginFormActionType) => void;
}

export enum LoginFormActionType {
    CheckUserType,
    PrivateLogin
}

const LoginForm: React.FC<ILoginForm> = (props) => {
    const [formActionType, setFormActionType] = useState<LoginFormActionType>(LoginFormActionType.CheckUserType);

    const isSM = useBreakpoint(BreakpointType.sm);
    const isMD = useBreakpoint(BreakpointType.md);
    const isLG = useBreakpoint(BreakpointType.lg);
    const isXL = useBreakpoint(BreakpointType.xl);
    const isXXL = useBreakpoint(BreakpointType.xxl);
    
    const onRenderDescription = (props?: ITextFieldProps | undefined, defaultRender?: ((props?: ITextFieldProps | undefined) => JSX.Element | null) | undefined): JSX.Element | null => {
        return (
            <Text variant="xSmall" style={{fontWeight: "bold"}}>
                {props!.description}
            </Text>
        );
    };
    
    return (
        <Stack
            className={privateFormContainer}
        >
            <form
                onSubmit={(ev) => {
                    ev.preventDefault();
                    props.handleSubmit(formActionType)
                }}
            >
                <Stack>
                    <TextField
                        label="Account"
                        type="email"
                        autoComplete="email"
                        placeholder={t('common:LoginBox:emailText')!}
                        styles={
                            isSM ? 
                                smLoginBoxTextFieldStyles :
                            isMD ?
                                mdLoginBoxTextFieldStyles :
                            isLG ?
                                lgLoginBoxTextFieldStyles :
                            isXL ?
                                xlLoginBoxTextFieldStyles :
                            isXXL ?
                                xxlLoginBoxTextFieldStyles 
                            :
                                loginBoxTextFieldStyles
                        }
                        value={props.username}
                        errorMessage={props.userNameErrorMessage}
                        invalid={(props.userNameErrorMessage! > '')}
                        onChange={(ev, newValue) => props.handleUsernameChange(newValue)}
                        autoFocus
                    />
                </Stack>
                <Stack
                    hidden={!props.showPrivateLoginBox}
                >
                    <TextField
                        autoComplete="current-password"
                        type='password'
                        label={t('common:LoginBox:passwordText')!}
                        placeholder={t('common:LoginBox:passwordText2')!}
                        styles={
                            isSM ? 
                                smLoginBoxTextFieldStyles :
                            isMD ?
                                mdLoginBoxTextFieldStyles :
                            isLG ?
                                lgLoginBoxTextFieldStyles :
                            isXL ?
                                xlLoginBoxTextFieldStyles :
                            isXXL ?
                                xxlLoginBoxTextFieldStyles 
                            :
                                loginBoxTextFieldStyles
                        }
                        value={props.password}
                        errorMessage={props.passwordErrorMessage}
                        invalid={(props.passwordErrorMessage! > '')}
                        onRenderDescription={onRenderDescription}
                        onChange={(ev, newValue) => props.handlePasswordChange(newValue)}
                        canRevealPassword 
                    />
                </Stack>
                <Stack>
                    <PrimaryButton
                        type="submit"
                        text={
                            props.showPrivateLoginBox ?
                                t('common:LoginBox:primaryButtonText2')!
                            :
                                t('common:LoginBox:primaryButtonText3')!
                        }
                        styles={
                            isSM ? 
                                smLoginButtonStyles :
                            isMD ?
                                mdLoginButtonStyles :
                            isLG ?
                                lgLoginButtonStyles :
                            isXL ?
                                xlLoginButtonStyles :
                            isXXL ?
                                xxlLoginButtonStyles
                            :
                                loginButtonStyles
                        }  
                        disabled={props.saveInProgress} 
                        toggle 
                        allowDisabledFocus
                        onClick={(ev) => {
                            if (props.showPrivateLoginBox)
                                setFormActionType(LoginFormActionType.PrivateLogin);
                            else
                                setFormActionType(LoginFormActionType.CheckUserType);
                        }}
                    />
                </Stack>
            </form>
            <Stack 
                hidden={!props.showPasswordDismiss}
                verticalFill
                verticalAlign="center"
                horizontalAlign="center"
                className={wrongPasswordContainerStyles}
            >
                <Stack>
                    <Text 
                        style={{ color: "red"}}
                    >
                        {t('common:LoginBox:wrongPassword')!}
                    </Text>
                </Stack>
                <Stack>
                    <Link 
                        style={{ position: "relative", marginTop: 5 }} 
                        onClick={props.handleUserResetPassword} 
                        underline
                    >
                        {t('common:LoginBox:forgotPasswordLinkText')!}
                        <Icon 
                            iconName="DoubleChevronRight" 
                            style={{
                                position: 'absolute', 
                                top: '50%', 
                                transform: 'translateY(-50%)', 
                                paddingLeft: '0.5dvh', 
                                color: '#2A7DE1' 
                            }} 
                        />
                    </Link>
                </Stack>
            </Stack>
        </Stack>
    );
}

const RegisterLink: React.FC = () => {
    const isSM = useBreakpoint(BreakpointType.sm);
    const isMD = useBreakpoint(BreakpointType.md);
    const isLG = useBreakpoint(BreakpointType.lg);
    const isXL = useBreakpoint(BreakpointType.xl);
    const isXXL = useBreakpoint(BreakpointType.xxl);

    return (
        <Stack>
            <Stack 
                horizontal
                verticalAlign="center"
                key="registerLink" 
                className={registerLinkStyles}
            >
                <Stack
                    horizontal
                >
                    <Text 
                        key={"accountNeeded"}
                        className={
                            isSM ? 
                                smSubTitleStyles :
                            isMD ?
                                mdSubTitleStyles :
                            isLG ?
                                lgSubTitleStyles :
                            isXL ?
                                xlSubTitleStyles :
                            isXXL ?
                                xxlSubTitleStyles
                            :
                                subTitleStyles
                        }
                    >
                        {t("common:LoginBox:accountNeeded")!}
                        &nbsp;
                        <Link 
                            style={{ position: "relative" }}
                            href="register" 
                            underline
                        >
                            {t("common:LoginBox:register")!}
                        </Link>
                        <Icon 
                            iconName="DoubleChevronRight" 
                            style={{
                                position: 'relative', 
                                top: '50%', 
                                transform: 'translateY(-50%)', 
                                paddingLeft: '0.5dvh', 
                                color: '#2A7DE1' 
                            }} 
                        />
                    </Text>
                </Stack>
            </Stack>
        </Stack>
    );
};

export const LoginBox: React.FC = (props) => {
    const sessionStateContext: ISessionStateContext = useContext(SessionStateContext);
    const [loaderMessage, setLoaderMessage] = useState(t('common:LoginBox:loader:message')!);
    const [logo, setLogo] = useState<string>();
    const [dataStatus, setDataStatus] = useState(SystemDataLoadingStatus.ToLoad);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [usernameErrorMessage, setUsernameErrorMessage] = useState("");
    const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
    const [saveInProgress, setSaveInProgress] = useState(false);
    const navigate = useNavigate();
    const [showPrivateLoginBox, setShowPrivateLoginBox] = useState(false);
    const [showPasswordDismiss, setShowPasswordDismiss] = useState(false);
    const [confirmButtonDisabled, setConfirmButtonDisabled] = useState(false);

    const isSM = useBreakpoint(BreakpointType.sm);
    const isMD = useBreakpoint(BreakpointType.md);
    const isLG = useBreakpoint(BreakpointType.lg);
    const isXL = useBreakpoint(BreakpointType.xl);
    const isXXL = useBreakpoint(BreakpointType.xxl);
    
    const getRequestedResource = (): string => {
        let requestedResource = HttpHelper.getParameter("resource");
        if ((requestedResource !== null) && (requestedResource !== ""))
            return atob(requestedResource);
        
        return '';
    }

    const getRedirectUrl = (login_hint?: string, prompt?: string) => {
        let redirectUrl = "https://login.microsoftonline.com/common/oauth2/authorize?client_id=46dd81d5-9a3d-4b14-8ecb-3d1812a62027&response_type=id_token&redirect_uri=" + window.location.origin + "/ms/afterauth&response_mode=fragment&scope=User.Read&nonce=678910";
        
        if (login_hint)
            redirectUrl += '&login_hint=' + login_hint;
        if (prompt)
            redirectUrl += '&prompt=' + prompt;

        let callbackUrl: string = getRequestedResource();
        if (callbackUrl)
            redirectUrl += "&state=" + btoa(callbackUrl);

        return redirectUrl;
    }

    const getEmailByParams = () => {
        let emailParam = HttpHelper.getParameter("email");
        if (emailParam){
            try {
                return atob(emailParam);
            }
            catch {
                return emailParam;
            }
        }

        return undefined;
    }
    
    const checkAuthenticationParameter = (ctxValue?: string) => {
        const url = new URL(window.location.toString());
        let ctx: string | null = null;

        if(ctxValue)
            ctx = ctxValue;
        else
            ctx = url.searchParams.get("ctx");
        
        if (ctx != null) {
            ctx = atob(ctx);
            
            let systemCore: SystemCore = new SystemCore();
            systemCore.authenticateUserFromParameter(ctx)
                .then((resp) => {
                    sessionStateContext.isAuthenticated = true;
                    navigate("/app");
                })
                .catch((err) => {
                    ctx = null;
                    ToastService.showMessage(MessageType.Error, err);
                    navigate("/login");
                });
        }
    };

    const resetState = () => {
        setUsernameErrorMessage("");
        setPasswordErrorMessage("");
        setConfirmButtonDisabled(false);
        setShowPrivateLoginBox(false);
    }
    
    const handleUsernameChange = (newValue?: string | undefined) => {
        setUsername(newValue!);
        if (!newValue!)
            setUsernameErrorMessage(t('common:LoginBox:emailMandatory')!);
        else
            setUsernameErrorMessage("");
    }

    const handlePasswordChange = (newValue?: string | undefined) => {
        setPassword(newValue!);
        if (!newValue)
            setPasswordErrorMessage(t('common:LoginBox:passwordMandatory')!);
        else
            setPasswordErrorMessage("");
    }

    const handleCheckUserType = (email?: string) => {
        if (!email) {
            setUsernameErrorMessage(t('common:LoginBox:emailMandatory')!);
            return;
        }
        
        setSaveInProgress(true);
        CommonFunctions.getUserType(email)
            .then((resp) => {
                switch(resp.actionType) {
                    case LoginActionType.Empty:
                        if(resp.userType === SystemUserType.AAD) {
                            window.location.replace(getRedirectUrl(email));
                        }
                        else {
                            setConfirmButtonDisabled(true);
                            setShowPrivateLoginBox(true);
                            setSaveInProgress(false);
                        }
                        break;
                    case LoginActionType.Ctx:
                        checkAuthenticationParameter(resp.message);
                        break;
                    case LoginActionType.Redirect:
                    case LoginActionType.SetPassword:
                        window.location.replace(resp.message);
                        break;
                    case LoginActionType.PrivateUserLoginForbidden:
                        window.location.replace(getRedirectUrl(email, 'select_account'));
                        break;
                }
            })
            .catch((err) => {
                ToastService.showMessage(MessageType.Error, err.message);
                setSaveInProgress(false);
            });
    }
    
    const handleLoginClick = () => {
        setDataStatus(SystemDataLoadingStatus.Loading);

        if (!username) {
            setUsernameErrorMessage(t('common:LoginBox:emailMandatory')!);
            setDataStatus(SystemDataLoadingStatus.Loaded);
            return;
        }
        if (!password) {
            setPasswordErrorMessage(t('common:LoginBox:passwordMandatory')!);
            setDataStatus(SystemDataLoadingStatus.Loaded);
            return;
        }
        
        setLoaderMessage(t('common:LoginBox:loader:text')!);
        setSaveInProgress(true);
        
        let systemCore: SystemCore = new SystemCore();
        let credential: UserCredential = {
            username: username,
            password: password
        };

        systemCore.checkUserPassword(credential)
            .then((resp) => {
                if (!resp)
                    {
                        setShowPasswordDismiss(true);
                        setPassword("");
                        setDataStatus(SystemDataLoadingStatus.Loaded);
                        setSaveInProgress(false);
                    }
                else {        
                    systemCore.authenticateUser(credential)
                    .then((resp) =>{
                        sessionStateContext.isAuthenticated = true;
                        let requestedResource = getRequestedResource();
                        if (requestedResource)
                            navigate(requestedResource);
                        else
                            navigate("/app");
                        
                        return true;
                    })
                    .catch((resp) => {
                        ToastService.showMessage(MessageType.Error, resp.error);
                        setUsername("");
                        setPassword("");
                        setDataStatus(SystemDataLoadingStatus.Loaded);
                        setSaveInProgress(false);
                        resetState();
                        
                        return false;
                    });
                }
            })
            .catch((err) => {
                console.log(err);
            })
    }
    
    const handleUserResetPassword = () => {
        setSaveInProgress(true);
        
        let promise = CommonFunctions.handlePasswordReset(username)
        .then(() => {
            setSaveInProgress(false);
        })
        .catch((err) => {
            setSaveInProgress(false);
        });

        ToastService.showPromiseMessage(promise, t('common:LoginBox:Text00002lbl')!, t('common:LoginBox:Text00001lbl')!, t('common:LoginBox:Text00003lbl')!)
    }

    const handleOnSubmit = (formActionType: LoginFormActionType) => {
        switch(formActionType) {
            case LoginFormActionType.CheckUserType:
                handleCheckUserType(username);
                break;
            case LoginFormActionType.PrivateLogin:
                handleLoginClick();
                break;
            default:
                ToastService.showMessage(MessageType.Error, t('common:LoginBox:Text00004lbl')!);
                break;
        }
    }

    useEffect(() => {
        setDataStatus(SystemDataLoadingStatus.Loading);
        if(sessionStateContext.isAuthenticated){
            navigate("/app");
            setDataStatus(SystemDataLoadingStatus.Loaded);
        }
        else {
            //Check if the link has a ctx value
            checkAuthenticationParameter();
            
            //Handle refresh token if present
            let systemCore = new SystemCore();
            if(systemCore.getUserToken() === "" && !sessionStateContext.isAuthenticated)
            {
                systemCore.authenticateUserFromRefreshToken().then(()=>{
                    sessionStateContext.isAuthenticated = true;
                    navigate("/app");
                })
            }

            //Get the system app public info
            SystemCore.getSystemAppPublicInfo(window.location.origin)
                .then((resp:any) => {
                    document.title = resp.appTitle;
                    SystemCore.setFavicons('data:image/png;base64,'+resp.appIcon);
                    setLogo('data:image/png;base64,'+resp.appLogo);
                    setDataStatus(SystemDataLoadingStatus.Loaded);
                })
                .catch((err) => {
                    setLogo(LogoSoSmart);
                    setDataStatus(SystemDataLoadingStatus.Loaded);
                });
            
            //Start email flow if email is present in the url
            let userNameParam = getEmailByParams();
            if (userNameParam) {
                setUsername(userNameParam);
                handleCheckUserType(userNameParam);
            }
        }
    }, []);

    if(!SystemCore.isFinishedDataStatus(dataStatus))
        return (
            <Loader 
                text={loaderMessage} 
            />
        );

    return (
        <Stack 
            horizontal
            verticalFill
            horizontalAlign="center"
            verticalAlign="center"
            className={loginBoxPageContainer}
        >
            <Stack
                className={
                    isSM ? 
                        smLoginBoxContainer :
                    isMD ?
                        mdLoginBoxContainer :
                    isLG ?
                        lgLoginBoxContainer :
                    isXL ?
                        xlLoginBoxContainer :
                    isXXL ?
                        xxlLoginBoxContainer
                    :
                        loginBoxContainer
                }
            >
                <Stack
                    horizontal
                    horizontalAlign="center"
                    className={loginLogoContainer}
                >
                    <Image 
                        src={logo} 
                        imageFit={ImageFit.cover}
                    />
                </Stack>
                <Stack
                    className={
                        isSM || isMD ? 
                            smallWelcomeContainerStyles 
                            : 
                            welcomeContainerStyles    
                        }
                >
                    <Stack 
                        verticalFill
                        verticalAlign="start" 
                        horizontalAlign="center"
                    >
                        <Text
                            className={
                                isSM ? 
                                    smTitleStyles :
                                isMD ?
                                    mdTitleStyles :
                                isLG ?
                                    lgTitleStyles :
                                isXL ?
                                    xlTitleStyles :
                                isXXL ?
                                    xxlTitleStyles
                                :
                                    titleStyles
                            }
                        >
                            {t('common:LoginBox:welcome')!}
                        </Text>
                        <Text
                            className={
                                isSM ? 
                                    smSubTitleStyles :
                                isMD ?
                                    mdSubTitleStyles :
                                isLG ?
                                    lgSubTitleStyles :
                                isXL ?
                                    xlSubTitleStyles :
                                isXXL ?
                                    xxlSubTitleStyles
                                :
                                    subTitleStyles
                            }
                        >
                            {t('common:LoginBox:welcome2')!}
                        </Text>
                    </Stack>
                </Stack>
                <LoginForm
                    username={username}
                    password={password}
                    userNameErrorMessage={usernameErrorMessage}
                    passwordErrorMessage={passwordErrorMessage}
                    saveInProgress={saveInProgress}
                    showPrivateLoginBox={showPrivateLoginBox}
                    showPasswordDismiss={showPasswordDismiss}
                    confirmButtonDisabled={confirmButtonDisabled}
                    handleUsernameChange={handleUsernameChange}
                    handlePasswordChange={handlePasswordChange}
                    handleUserResetPassword={handleUserResetPassword}
                    handleSubmit={handleOnSubmit}
                />
                <RegisterLink />
            </Stack>
        </Stack>
    );
}